import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AlertCircle, CheckCircle2 } from "lucide-react";

interface RejectionWarningModalProps {
  isOpen: boolean;
  candidateName: string | null;
  onConfirm: (notes: string) => void;
  onCancel: () => void;
  isProcessing?: boolean;
  error?: string | null;
  success?: boolean;
}

export function RejectionWarningModal({ 
  isOpen, 
  candidateName, 
  onConfirm, 
  onCancel,
  isProcessing = false,
  error = null,
  success = false
}: RejectionWarningModalProps) {
  const [rejectionNotes, setRejectionNotes] = useState("");

  // Reset notes when modal closes
  useEffect(() => {
    if (!isOpen) {
      setRejectionNotes("");
    }
  }, [isOpen]);

  const handleCancel = () => {
    if (isProcessing) return; // Prevent closing when processing
    onCancel();
  };

  const handleConfirm = () => {
    onConfirm(rejectionNotes.trim());
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && !isProcessing && !success && handleCancel()}>
      <AlertDialogContent
        overlayClassName="!z-[9998]"
        className="!z-[9999]"
        {...((isProcessing || success) ? {
          onPointerDownOutside: (e: Event) => e.preventDefault(),
          onEscapeKeyDown: (e: KeyboardEvent) => e.preventDefault()
        } : {})}
      >
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <AlertDialogTitle className="space-grotesk text-red-600 text-left">
                Reject Candidate?
              </AlertDialogTitle>
            </div>
          </div>
          <AlertDialogDescription className="space-grotesk text-left">
            You are about to reject <strong>{candidateName}</strong> for this position. 
            This action will permanently change their status to "Rejected" and they will no longer be considered for this role.
            <br /><br />
            Are you sure you want to continue?
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                <span className="text-red-600 text-xs font-bold">!</span>
              </div>
              <div className="text-sm text-red-800">
                <p className="font-medium">Error Processing Rejection</p>
                <p className="text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success Indicator */}
        {success && !error && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <div className="text-sm text-green-800">
                <p className="font-medium">Candidate rejected successfully!</p>
                <p className="text-green-600">Closing in a moment...</p>
              </div>
            </div>
          </div>
        )}

        {/* Processing Indicator */}
        {isProcessing && !success && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <div className="text-sm text-blue-800">
                <p className="font-medium">Processing rejection...</p>
                <p className="text-blue-600">This may take a few moments.</p>
              </div>
            </div>
          </div>
        )}

        {/* Rejection Notes */}
        {!success && !isProcessing && (
          <div className="mb-4">
            <Label htmlFor="rejectionNotes" className="text-sm font-medium space-grotesk mb-2 block">
              Feedback for referrer *
            </Label>
            <Textarea
              id="rejectionNotes"
              value={rejectionNotes}
              onChange={(e) => setRejectionNotes(e.target.value)}
              placeholder="e.g., Experience level, salary mismatch, skill gap..."
              className="resize-none font-manrope"
              rows={3}
            />
            <p className="text-xs text-gray-500 mt-1">
              Required. This feedback will be shared with the referrer via email and in their dashboard.
            </p>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button
              variant="secondary"
              onClick={handleCancel}
              className="space-grotesk"
              disabled={isProcessing || success}
            >
              Cancel
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              className="space-grotesk"
              disabled={isProcessing || success || !rejectionNotes.trim()}
            >
              {success ? "Success!" : isProcessing ? "Rejecting..." : "Reject"}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
